import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { Home } from 'lucide-react';
import { createContext, useContext, useEffect } from 'react';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { ProgramAccessGate } from '../components/access/ProgramAccessGate';
import { PageBody } from '../components/layout';
import { useLayoutStore } from '../lib/stores/LayoutStore';
import { ErrorState } from '../pages/Delivery/_shared/components/PageStates';
import { POTENTIAL_ISSUES_COPY, WAIVERS_TAILORING_COPY } from '../pages/Delivery/_shared/constants/sectionCopy';
import { CmDrawers } from '../pages/Delivery/Deliverables/components/CmDrawers';
import { GlossaryProvider } from '../pages/Delivery/Glossary/GlossaryProvider';
import { useProgram } from '../pages/Delivery/Programs/hooks';
const ProgramContext = /*#__PURE__*/ createContext(null);
export function useProgramContext() {
    const ctx = useContext(ProgramContext);
    if (!ctx) throw new Error('useProgramContext must be used within ProgramLayout');
    return ctx;
}
// Sub-route → breadcrumb label. Falls back to a title-cased segment.
const SECTION_LABELS = {
    '': 'Overview',
    'activation-gate': 'Activation Gate',
    workroom: 'Chat',
    roles: 'Roles',
    companies: 'Companies',
    contacts: 'Contacts',
    clins: 'CLINs',
    workstreams: 'Teams',
    deliverables: 'Deliverables',
    settings: 'Settings',
    capabilities: 'Capabilities',
    'optional-tasks': 'Optional Tasks',
    places: 'Places',
    periods: 'Periods',
    audits: 'Audits',
    'waivers-tailoring': WAIVERS_TAILORING_COPY.label,
    // Pre-rename alias — the redirect can render this breadcrumb for one
    // frame, so it must show the new label, not the old word.
    deviations: WAIVERS_TAILORING_COPY.label,
    quality: 'Quality',
    'potential-issues': POTENTIAL_ISSUES_COPY.label,
    // Pre-rename alias — see the 'waivers-tailoring' comment above.
    risk: POTENTIAL_ISSUES_COPY.label,
    'ops-audit': 'History',
    touchpoints: 'Touchpoints',
    financial: 'Financial',
    docs: 'Docs',
    glossary: 'Glossary'
};
function titleCase(segment) {
    return segment.split('-').map((w)=>w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}
export function ProgramLayout() {
    const { id = '' } = useParams();
    const location = useLocation();
    const setBreadcrumbItems = useLayoutStore((s)=>s.setBreadcrumbItems);
    const { data, isLoading, error, refetch } = useProgram(id);
    const program = data?.program;
    const programLabel = program?.programNameShort || program?.programNameFull || 'Program';
    // The generic metadata band is suppressed for ALL /delivery routes by
    // DeliveryLayout (every delivery page owns an in-content header now) — no
    // per-layout toggle here, or unmounting a program route would flash the
    // band back over sibling delivery pages.
    // Feed the topbar a program-aware trail: Home / Delivery / Programs /
    // <program> / <section>. The id is opaque (hidden by the generic generator)
    // and the program name only lives in this route's data.
    useEffect(()=>{
        if (!program) return;
        const rest = location.pathname.split(`/delivery/programs/${id}`)[1] ?? '';
        const section = rest.replace(/^\//, '').split('/')[0] ?? '';
        setBreadcrumbItems([
            {
                label: 'Home',
                href: '/',
                icon: /*#__PURE__*/ _jsx(Home, {
                    className: "h-3 w-3"
                })
            },
            {
                label: 'Delivery',
                href: '/delivery'
            },
            {
                label: 'Programs',
                href: '/delivery/programs'
            },
            {
                label: programLabel,
                href: `/delivery/programs/${id}`
            },
            {
                label: SECTION_LABELS[section] ?? titleCase(section)
            }
        ]);
        return ()=>setBreadcrumbItems(null);
    }, [
        id,
        programLabel,
        program,
        location.pathname,
        setBreadcrumbItems
    ]);
    if (!isLoading && !data?.program) {
        // Denied and nonexistent both arrive as NotFound — the server deliberately
        // doesn't reveal which. Anything else is a real failure and gets a retry.
        const notFound = error instanceof ConnectError && error.code === Code.NotFound;
        if (!notFound && error) {
            return /*#__PURE__*/ _jsx(PageBody, {
                children: /*#__PURE__*/ _jsx(ErrorState, {
                    error: error,
                    onRetry: ()=>void refetch()
                })
            });
        }
        return /*#__PURE__*/ _jsx(PageBody, {
            children: /*#__PURE__*/ _jsx(ProgramAccessGate, {
                programId: id
            })
        });
    }
    return /*#__PURE__*/ _jsx(ProgramContext.Provider, {
        value: {
            programId: id,
            program: data?.program,
            canEdit: data?.canEdit ?? false,
            canManageContacts: data?.canManageContacts ?? false,
            isLoading
        },
        children: /*#__PURE__*/ _jsxs(GlossaryProvider, {
            programId: data?.program ? id : '',
            projectId: data?.program?.zouProjectId,
            children: [
                /*#__PURE__*/ _jsx(Outlet, {}),
                /*#__PURE__*/ _jsx(CmDrawers, {
                    programId: id
                })
            ]
        })
    });
}
